/**
 * Declarative schema for the Integrations (connectors) settings page.
 *
 * Single source of truth for both the provider grid and the connector config
 * dialog (RfConnectors / RfConnectorDialog): every provider's card metadata and
 * the ordered list of fields rendered in its dialog. Derived from the previous
 * hand-written 31-block template; `src/tests/utils/connectors/providerSchemas.test.js`
 * pins the per-provider field signatures so any accidental reorder, rename or
 * dropped field fails before the config that reaches the backend (`app.providers`)
 * can change shape.
 *
 * Field binding: unless stated otherwise a field binds `formProviders.<schemaKey>.<key>`
 * (the dialog's draft copy of `app.providers`). Overrides:
 * - `provider` — bind `formProviders.<provider>.<key>` instead (e.g. push providers
 *   that write their credentials into the `redfast` / `google_play` config).
 * - `bind: "flags"` — bind `app.flags.<key>` directly (bypasses the form draft).
 * - `bind: "appProviders"` — bind `app.providers.<provider>.<key>` directly.
 *
 * `flagDisabled` warning banners are not part of the schema: the dialog renders the
 * banner automatically for any provider whose card `flag` is not enabled.
 *
 * @typedef {"switch"|"text"|"select"|"checkbox"|"heading"|"note"|"actions"|"custom"} FieldType
 *
 * @typedef {Object} FieldDescriptor
 * @property {FieldType} type - Renderer to use.
 * @property {string} [key] - Provider config key (form field name).
 * @property {string} [provider] - Alternative providers object to bind (default: schema key).
 * @property {"flags"|"appProviders"} [bind] - Non-draft binding target.
 * @property {string} [title] - Input label (text/select).
 * @property {string} [placeholder] - Input placeholder (defaults to title in the dialog).
 * @property {string} [label] - Visible label (switch/checkbox).
 * @property {string} [hint] - Hint line under the input.
 * @property {boolean} [textarea] - Render a text field as a textarea.
 * @property {boolean} [readonly] - Input is always read-only (e.g. Stripe account id).
 * @property {Array<string|{text: string, value: string}>} [items] - Select options (RfSelect shape).
 * @property {string|boolean} [default] - Value seeded into the form when unset (e.g. Recurly region).
 * @property {string} [text] - Static text (heading/note).
 * @property {boolean} [events] - Note appends the shared analytics events list.
 * @property {string|string[]} [actions] - Actions table source: connector-actions provider key or literal list.
 * @property {boolean} [inside] - Actions table renders inside `connector-edit-div` (default: sibling below it).
 * @property {string} [component] - Custom renderer id (custom fields).
 * @property {string} [channel] - Push channel for the test-push-sender custom field.
 * @property {1|2} [col] - Salesforce two-column layout column (meta `columns: true`).
 */

/** Settings page sections the provider cards are grouped under. */
export const SECTIONS = [
  { key: "crm", name: "CRM" },
  { key: "billing", name: "Billing" },
  { key: "analytics", name: "Analytics" },
  { key: "push", name: "Push Notifications" },
  { key: "other", name: "Other" },
];

/** Recurly API regions; values match the recurly gem's `:us` / `:eu` clients. RfSelect item shape. */
export const RECURLY_REGIONS = [
  { text: "US", value: "us" },
  { text: "EU", value: "eu" },
];

/** AWS regions accepted for Pinpoint (Amazon ADM push). */
export const AWS_REGIONS = [
  "us-east-1",
  "us-west-2",
  "ap-south-1",
  "ap-norhteast-1",
  "ap-northeast-2",
  "ap-southeast-1",
  "ap-southeast-2",
  "ca-central-1",
  "eu-central-1",
  "eu-west-1",
  "eu-west-2",
];

/** Events the client-side analytics connectors (adobe_client, ga_client, amplitude_client) listen for. */
export const ANALYTICS_EVENTS = [
  "Recurly Engage Prompt Impression",
  "Recurly Engage Prompt Click",
  "Recurly Engage Prompt Click2",
  "Recurly Engage Prompt Dismiss",
  "Recurly Engage Prompt Decline",
  "Recurly Engage Prompt Timeout",
  "Recurly Engage Prompt Holdout",
];

export const MIXPANEL_ACTIONS = [
  "Send events to Mixpanel",
  "Send user traits to Mixpanel",
  "Receive segments from Mixpanel",
  "Receive user traits from Mixpanel",
  "Receive events from Mixpanel",
];

export const ORDERGROOVE_ACTIONS = [
  "Cancel Subscription",
  "Reactivate Subscription",
  "Change Subscription Quantity",
  "Change Subscription Frequency",
  "Change Subscription Product",
  "Change Subscription Next Order Date",
  "Change Subscription Renewal",
  "Skip Subscription Order",
  "Create One Time Incentive",
  "Delay 1-Click Action",
  "Reactivate 1-Click Action",
];

/**
 * Provider cards + dialog field definitions, in grid display order.
 *
 * Note: `vindicia` is unreachable today (its card was commented out of the legacy grid)
 * and is marked `hidden` — excluded from the grid and deep links.
 * `figma` has no `fields` — its dialog is a dedicated component (RfFigmaConnector).
 *
 * @type {Array<{key: string, name: string, type: string, src: string, width: string, flag?: string, position?: number, columns?: boolean, bare?: boolean, hidden?: boolean, fields?: FieldDescriptor[]}>}
 */
export const PROVIDER_SCHEMAS = [
  {
    key: "sendgrid",
    name: "Sendgrid",
    type: "crm",
    src: new URL("@/assets/images/logos/sendgrid.png", import.meta.url).href,
    width: "90",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: "sendgrid" },
    ],
  },
  {
    key: "salesforce",
    name: "Salesforce",
    type: "crm",
    src: new URL("@/assets/images/logos/salesforce.png", import.meta.url).href,
    width: "90",
    columns: true,
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "username", title: "USERNAME", col: 1 },
      { type: "text", key: "password", title: "PASSWORD", col: 1 },
      { type: "text", key: "security_token", title: "SECURITY TOKEN", col: 1 },
      { type: "text", key: "client_id", title: "CLIENT ID", col: 1 },
      { type: "text", key: "client_secret", title: "CLIENT SECRET", col: 1 },
      {
        type: "text",
        key: "marketing_cloud_client_id",
        title: "MARKETING CLOUD CLIENT ID",
        placeholder: " ",
        col: 2,
      },
      {
        type: "text",
        key: "marketing_cloud_client_secret",
        title: "MARKETING CLOUD CLIENT SECRET",
        placeholder: " ",
        col: 2,
      },
      {
        type: "text",
        key: "base_url",
        title: "MARKETING CLOUD BASE URL",
        placeholder: " ",
        col: 2,
      },
      {
        type: "text",
        key: "auth_base_url",
        title: "MARKETING CLOUD AUTH BASE URL",
        placeholder: " ",
        col: 2,
      },
      {
        type: "text",
        key: "soap_base_url",
        title: "MARKETING CLOUD SOAP BASE URL",
        placeholder: " ",
        col: 2,
      },
      { type: "text", key: "from_email", title: "FROM EMAIL", col: 2 },
      { type: "actions", actions: "salesforce" },
    ],
  },
  {
    key: "braze",
    name: "Braze",
    type: "crm",
    src: new URL("@/assets/images/logos/logo-braze.svg", import.meta.url).href,
    width: "65",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "base_url", title: "BASE URL" },
      { type: "text", key: "api_key", title: "API KEY" },
      { type: "actions", actions: "braze" },
    ],
  },
  {
    key: "apple",
    name: "Apple",
    type: "push",
    src: new URL("@/assets/images/logos/logo-apple.png", import.meta.url).href,
    width: "90",
    bare: true,
    fields: [
      {
        type: "text",
        key: "pinpoint_apns_bundle_id",
        provider: "redfast",
        title: "Apple Push Notifications Service Bundle ID",
        placeholder: "Apple Push Notifications Service Bundle ID",
      },
      {
        type: "text",
        key: "pinpoint_apns_team_id",
        provider: "redfast",
        title: "Apple Push Notifications Service Team ID",
        placeholder: "Apple Push Notifications Service Team ID",
      },
      {
        type: "text",
        key: "pinpoint_apns_token_key",
        provider: "redfast",
        title: "Apple Push Notifications Service Token Key",
        placeholder: "Apple Push Notifications Service Token Key",
        textarea: true,
      },
      {
        type: "text",
        key: "pinpoint_apns_token_key_id",
        provider: "redfast",
        title: "Apple Push Notifications Service Token Key ID",
        placeholder: "Apple Push Notifications Service Token Key ID",
      },
      { type: "custom", component: "applePushRow", key: "active", provider: "apple" },
      { type: "custom", component: "appleProductIds" },
    ],
  },
  {
    key: "recurly",
    name: "Recurly",
    type: "billing",
    src: new URL("@/assets/images/logos/recurly.png", import.meta.url).href,
    width: "120",
    position: 0,
    fields: [
      { type: "switch", key: "active" },
      {
        type: "select",
        key: "region",
        title: "Region",
        placeholder: "Region",
        items: RECURLY_REGIONS,
        default: "us",
        hint: "Non-US Recurly accounts (e.g. EU) must select their region here. Defaults to US.",
      },
      { type: "text", key: "api_key", title: "Private API Key", placeholder: "sk_..." },
      {
        type: "text",
        key: "public_key",
        title: "Public Key (for Recurly.js)",
        placeholder: "ewr1-...",
      },
      { type: "checkbox", key: "lookup_by_account_code", label: "Use Account Code" },
      {
        type: "checkbox",
        key: "multi_subscription_webhooks",
        label: "Multi Subscription Webhooks",
      },
      { type: "checkbox", key: "auto_ingest", label: "Process Daily Updates" },
      { type: "actions", actions: "recurly" },
    ],
  },
  {
    key: "recurly_commerce",
    name: "Recurly Commerce",
    type: "billing",
    src: new URL("@/assets/images/logos/recurly_commerce.png", import.meta.url).href,
    width: "120",
    flag: "recurly_commerce_connector_enabled",
    position: 1,
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: "recurly_commerce" },
    ],
  },
  {
    key: "stripe",
    name: "Stripe",
    type: "billing",
    src: new URL("@/assets/images/logos/stripe.png", import.meta.url).href,
    width: "60",
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      { type: "custom", component: "stripeConnect" },
      {
        type: "text",
        key: "account_id",
        title: "ACCOUNT ID",
        placeholder: "Stripe Connect Account ID",
        readonly: true,
        hint: "Go to https://dashboard.stripe.com/account/applications to manage your account",
      },
      { type: "text", key: "secret_key", title: "SECRET KEY", placeholder: "SECRET KEY" },
      { type: "actions", actions: "stripe" },
    ],
  },
  {
    // Unreachable: card commented out of the legacy grid. `hidden` keeps it out of the
    // grid and deep links while preserving the dialog definition for reference.
    key: "vindicia",
    name: "Vindicia",
    type: "billing",
    src: new URL("@/assets/images/logos/vindicia.png", import.meta.url).href,
    width: "82",
    hidden: true,
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "base_url", title: "URL", placeholder: "URL" },
      { type: "text", key: "username", title: "USERNAME", placeholder: "USERNAME" },
      { type: "text", key: "password", title: "PASSWORD", placeholder: "PASSWORD" },
      { type: "actions", actions: "vindicia" },
    ],
  },
  {
    key: "braintree",
    name: "Braintree",
    type: "billing",
    src: new URL("@/assets/images/logos/braintree.gif", import.meta.url).href,
    width: "90",
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      {
        type: "select",
        key: "environment",
        title: "ENVIRONMENT",
        placeholder: "ENVIRONMENT",
        items: ["sandbox", "production"],
      },
      { type: "text", key: "merchant_id", title: "MERCHANT ID", placeholder: "MERCHANT ID" },
      { type: "text", key: "public_key", title: "PUBLIC KEY", placeholder: "PUBLIC KEY" },
      { type: "text", key: "private_key", title: "PRIVATE KEY", placeholder: "PRIVATE KEY" },
      { type: "actions", actions: "braintree" },
    ],
  },
  {
    key: "ordergroove",
    name: "Ordergroove",
    type: "billing",
    src: new URL("@/assets/images/logos/ordergroove.png", import.meta.url).href,
    width: "200",
    flag: "ordergroove",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: ORDERGROOVE_ACTIONS },
    ],
  },
  {
    key: "zendesk",
    name: "Zendesk",
    type: "crm",
    src: new URL("@/assets/images/logos/zendesk.png", import.meta.url).href,
    width: "80",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "domain", title: "DOMAIN", placeholder: "DOMAIN" },
      { type: "text", key: "username", title: "USERNAME", placeholder: "USERNAME" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: "zendesk" },
    ],
  },
  {
    key: "freshdesk",
    name: "Freshdesk",
    type: "crm",
    src: new URL("@/assets/images/logos/freshdesk.png", import.meta.url).href,
    width: "90",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "domain", title: "DOMAIN", placeholder: "DOMAIN" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: "freshdesk" },
    ],
  },
  {
    key: "shopify",
    name: "Shopify",
    type: "billing",
    src: new URL("@/assets/images/logos/shopify.png", import.meta.url).href,
    width: "88",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "shop_url", title: "SHOP URL", placeholder: "SHOP URL" },
      { type: "actions", actions: "shopify" },
    ],
  },
  {
    key: "zuora",
    name: "Zuora",
    type: "billing",
    src: new URL("@/assets/images/logos/zuora.png", import.meta.url).href,
    width: "80",
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_url", title: "API URL", placeholder: "API URL" },
      { type: "text", key: "client_id", title: "CLIENT ID", placeholder: "CLIENT ID" },
      { type: "text", key: "client_secret", title: "CLIENT SECRET", placeholder: "CLIENT SECRET" },
      { type: "actions", actions: "zuora" },
    ],
  },
  {
    key: "segment",
    name: "Segment",
    type: "crm",
    src: new URL("@/assets/images/logos/segment_io.png", import.meta.url).href,
    width: "88",
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      { type: "heading", text: "Access Tokens" },
      {
        type: "text",
        key: "unify_access_token",
        title: "Unify Access Token",
        placeholder: "Unify Access Token",
      },
      {
        type: "text",
        key: "unify_space_id",
        title: "Unify Space ID",
        placeholder: "Unify Space ID",
      },
      { type: "heading", text: "Destination Details" },
      {
        type: "text",
        key: "lambda_aws_arn",
        title: "Lambda AWS ARN",
        placeholder: "Lambda AWS ARN",
      },
      { type: "text", key: "aws_region", title: "AWS Region", placeholder: "AWS Region" },
      { type: "text", key: "lambda_aws_role", title: "AWS Role", placeholder: "AWS Role" },
      {
        type: "text",
        key: "lambda_aws_client_context",
        title: "Client Context",
        placeholder: "Client Context",
      },
      {
        type: "text",
        key: "lambda_aws_external_id",
        title: "External Id",
        placeholder: "External Id",
      },
      { type: "text", key: "lambda_aws_log_type", title: "Log Type", placeholder: "Log Type" },
    ],
  },
  {
    key: "chargify",
    name: "Chargify",
    type: "billing",
    src: new URL("@/assets/images/logos/chargify.png", import.meta.url).href,
    width: "98",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "subdomain", title: "DOMAIN", placeholder: "DOMAIN" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: "chargify" },
    ],
  },
  {
    key: "mixpanel",
    name: "Mixpanel",
    type: "analytics",
    src: new URL("@/assets/images/logos/mixpanel.png", import.meta.url).href,
    width: "98",
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      {
        type: "text",
        key: "username",
        title: "Service Account Username",
        placeholder: "Service Account Username",
      },
      {
        type: "text",
        key: "password",
        title: "Service Account Password",
        placeholder: "Service Account Password",
      },
      { type: "text", key: "api_token", title: "Project Token", placeholder: "Project Token" },
      { type: "text", key: "project_id", title: "Project ID", placeholder: "Project ID" },
      { type: "actions", actions: MIXPANEL_ACTIONS, inside: true },
    ],
  },
  {
    key: "google_maps",
    name: "Google Maps",
    type: "other",
    src: new URL("@/assets/images/logos/google-maps-platform.png", import.meta.url).href,
    width: "150",
    fields: [
      { type: "switch", key: "google_maps_active", bind: "flags" },
      {
        type: "text",
        key: "maps_api_key",
        provider: "google_play",
        title: "API KEY",
        placeholder: "API KEY",
      },
      {
        type: "text",
        key: "package_name",
        provider: "google_play",
        title: "PACKAGE NAME",
        placeholder: "PACKAGE NAME",
      },
      {
        type: "text",
        key: "ingest_client_id",
        provider: "google_play",
        title: "CLIENT ID",
        placeholder: "CLIENT ID",
      },
      {
        type: "text",
        key: "ingest_client_email",
        provider: "google_play",
        title: "CLIENT EMAIL",
        placeholder: "CLIENT EMAIL",
      },
      {
        type: "text",
        key: "ingest_private_key",
        provider: "google_play",
        title: "PRIVATE KEY",
        placeholder: "PRIVATE KEY",
      },
    ],
  },
  {
    key: "figma",
    name: "Figma",
    type: "other",
    flag: "figma_import_enabled",
    src: new URL("@/assets/images/logos/figma.png", import.meta.url).href,
    width: "90",
  },
  {
    key: "active_campaign",
    name: "Active Campaign",
    type: "crm",
    src: new URL("@/assets/images/logos/activecampaign_logo.png", import.meta.url).href,
    width: "150",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "text", key: "subdomain", title: "Subdomain", placeholder: "Subdomain" },
    ],
  },
  {
    key: "iterable",
    name: "Iterable",
    type: "crm",
    src: new URL("@/assets/images/logos/iterable_logo.png", import.meta.url).href,
    width: "150",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "actions", actions: "iterable" },
    ],
  },
  {
    key: "cleeng",
    name: "Cleeng",
    type: "billing",
    src: new URL("@/assets/images/logos/cleeng.svg", import.meta.url).href,
    width: "150",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "checkbox", key: "sync_events", label: "Sync Billing Events" },
      { type: "actions", actions: "cleeng", inside: true },
    ],
  },
  {
    key: "chargebee",
    name: "Chargebee",
    type: "billing",
    src: new URL("@/assets/images/logos/chargebee.svg", import.meta.url).href,
    width: "120",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "site", title: "SITE", placeholder: "your-site-name" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "checkbox", key: "auto_ingest", label: "Process Daily Updates" },
      { type: "actions", actions: "chargebee" },
    ],
  },
  {
    key: "amazon_adm",
    name: "Amazon",
    type: "push",
    src: new URL("@/assets/images/logos/logo-amazon.png", import.meta.url).href,
    width: "150",
    fields: [
      {
        type: "select",
        key: "pinpoint_region",
        provider: "redfast",
        title: "Region",
        placeholder: "Region",
        items: AWS_REGIONS,
      },
      {
        type: "text",
        key: "pinpoint_external_id",
        provider: "redfast",
        title: "Role External Id (optional)",
        placeholder: "Role External Id (optional)",
      },
      {
        type: "text",
        key: "pinpoint_external_arn",
        provider: "redfast",
        title: "Role ARN",
        placeholder: "Role ARN",
      },
      {
        type: "text",
        key: "pinpoint_external_event_stream_arn",
        provider: "redfast",
        title: "Event Stream ARN",
        placeholder: "Event Stream ARN",
      },
      {
        type: "text",
        key: "pinpoint_external_event_role_arn",
        provider: "redfast",
        title: "Event Stream Role ARN",
        placeholder: "Event Stream Role ARN",
      },
      {
        type: "text",
        key: "pinpoint_adm_client_id",
        provider: "redfast",
        title: "Amazon Device Messaging Client ID",
        placeholder: "Amazon Device Messaging Client ID",
      },
      {
        type: "text",
        key: "pinpoint_adm_client_secret",
        provider: "redfast",
        title: "Amazon Device Messaging Client Secret",
        placeholder: "Amazon Device Messaging Client Secret",
      },
      { type: "custom", component: "testPushSender", channel: "adm" },
    ],
  },
  {
    key: "google_fcm",
    name: "Google",
    type: "push",
    src: new URL("@/assets/images/logos/fcm.png", import.meta.url).href,
    width: "150",
    fields: [
      {
        type: "text",
        key: "pinpoint_fcm_service_json",
        provider: "redfast",
        title: "Firebase Cloud Messaging Service JSON",
        placeholder: "Firebase Cloud Messaging Service JSON",
        textarea: true,
      },
      { type: "custom", component: "testPushSender", channel: "gcm" },
    ],
  },
  {
    key: "adobe_client",
    name: "Adobe Analytics (Client)",
    type: "analytics",
    src: new URL("@/assets/images/logos/adobe_analytics_client.png", import.meta.url).href,
    width: "150",
    bare: true,
    flag: "adobe_client_export",
    fields: [
      { type: "switch", key: "active" },
      {
        type: "text",
        key: "org_id",
        title: "Org ID",
        placeholder: "1234567890ABCDEF12345678@AdobeOrg",
      },
      {
        type: "text",
        key: "data_stream_id",
        title: "Datastream ID",
        placeholder: "abcd1234-abcd-1234-abcd-12345678abcd",
      },
      {
        type: "checkbox",
        key: "delay_pings_until_load",
        bind: "appProviders",
        label: "Delay prompts until Adobe Alloy is loaded and configured",
      },
      {
        type: "note",
        text: "Omit the Org ID and Datastream ID settings if you are including and configuring the Adobe Alloy script yourself",
        events: true,
      },
    ],
  },
  {
    key: "adobe",
    name: "Adobe Analytics (Server)",
    type: "analytics",
    src: new URL("@/assets/images/logos/adobe_analytics_server.png", import.meta.url).href,
    width: "150",
    flag: "adobe_prompt_interaction_export",
    fields: [
      { type: "text", key: "identity_map_name", title: "Identity Map Name", placeholder: "rf_vid" },
      {
        type: "text",
        key: "event_type",
        title: "Event Type",
        placeholder: "web.webpagedetails.pageViews",
      },
      {
        type: "text",
        key: "instance_name",
        title: "Instance Name",
        placeholder: "redfasttechnologypartner",
      },
      {
        type: "text",
        key: "data_stream_id",
        title: "Data Stream ID",
        placeholder: "dbe419b3-8acc-4bcd-b8c0-c9b8670c0d1e",
      },
    ],
  },
  {
    key: "ga_client",
    name: "Google Analytics",
    type: "analytics",
    src: new URL("@/assets/images/logos/google_analytics.png", import.meta.url).href,
    width: "140",
    bare: true,
    flag: "ga_client_export",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "tag_id", title: "Tag ID", placeholder: "G-ABCDABCD12" },
      {
        type: "note",
        text: "Omit the Tag ID setting if you are including and configuring gtag yourself",
        events: true,
      },
    ],
  },
  {
    key: "amplitude_client",
    name: "Amplitude",
    type: "analytics",
    src: new URL("@/assets/images/logos/amplitude.png", import.meta.url).href,
    width: "140",
    bare: true,
    flag: "amplitude_client_export",
    fields: [
      { type: "switch", key: "active" },
      {
        type: "text",
        key: "api_key",
        title: "API Key",
        placeholder: "abcdef1234567890abcdef1234567890",
      },
      {
        type: "note",
        text: "Omit the API Key setting if you are including and configuring the Amplitude script yourself",
        events: true,
      },
    ],
  },
  {
    key: "amplitude",
    name: "Amplitude (Server)",
    type: "analytics",
    src: new URL("@/assets/images/logos/amplitude.png", import.meta.url).href,
    width: "140",
    flag: "amplitude_event_export",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      {
        type: "text",
        key: "base_url",
        title: "BASE URL (optional)",
        placeholder: "https://api2.amplitude.com",
      },
    ],
  },
  {
    key: "mparticle",
    name: "Mparticle",
    type: "analytics",
    src: new URL("@/assets/images/logos/logo-mparticle.svg", import.meta.url).href,
    width: "120",
    flag: "mparticle_event_export",
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API KEY", placeholder: "API KEY" },
      { type: "text", key: "api_secret", title: "API SECRET", placeholder: "API SECRET" },
    ],
  },
  {
    key: "npaw",
    name: "NPAW",
    type: "analytics",
    src: new URL("@/assets/images/logos/npaw.png", import.meta.url).href,
    width: "120",
    bare: true,
    fields: [
      { type: "switch", key: "active" },
      { type: "text", key: "api_key", title: "API Key" },
      { type: "text", key: "account_code", title: "Account Code" },
      { type: "text", key: "metric_id", title: "Metric ID", placeholder: "metric_abcd1234" },
    ],
  },
];

/**
 * Finds the schema entry for a provider key.
 *
 * @param {string} key - Provider key (e.g. "recurly").
 * @returns {Object|undefined} The schema entry, or undefined for unknown keys.
 */
export const providerSchema = key => PROVIDER_SCHEMAS.find(provider => provider.key === key);
